import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export const LoadState = Object.freeze({
  UNDEFINED: 'UNDEFINED', // (1) client has never fetched data from the server and not fetching data now - show refresh button
  INITIAL_LOAD: 'INITIAL_LOAD', // (2) initial load in progress - show skeleton
  INITIAL_LOAD_ERROR: 'INITIAL_LOAD_ERROR', // (1) initial load failed - show 'sorry try again'
  EMPTY: 'EMPTY', // (3) data has been loaded, but server has empty data collection - show add/setup button
  EMPTY_UPDATE: 'EMPTY_UPDATE', // (2) incremental data update for empty data collection is in progress - show skeleton
  EMPTY_UPDATE_ERROR: 'EMPTY_UPDATE_ERROR', // (1) incremental data update for empty data collection failed - show 'try again'
  READY: 'READY', // (4) we have some data and it is up to date - show corresponding feature
  READY_UPDATE: 'READY_UPDATE', // (5) data is ready and is updating now - progress (optional)
  READY_UPDATE_ERROR: 'READY_UPDATE_ERROR', // (6) data is ready and update failed - try again (optional)
});

// presets
export const useLoadRequiredState = (state) => state === LoadState.UNDEFINED;
export const useEmptyState = (state) => state === LoadState.EMPTY;

// show 'error - try again' option
// client tried to fetch data, but no local data because of error
// it's important to explain to user that we might have the data on the server, but can't fetch it now
export const tryAgainStates = [LoadState.INITIAL_LOAD_ERROR, LoadState.EMPTY_UPDATE_ERROR];
export const useTryAgainRequiredState = (state) => useMemo(() => tryAgainStates.includes(state), [state]);

// (optional) show error linear progress
// some error occurred
export const errorStates = [...tryAgainStates, LoadState.READY_UPDATE_ERROR];
export const useErrorState = (state) => useMemo(() => errorStates.includes(state), [state]);
// show 'load initial data' option (use when client setup required - like need to add account first to get transactions)
// data was never successfully loaded for some reason
// it's important to explain to user that we might have the data on the server
export const updateRequiredStates = [...tryAgainStates, LoadState.UNDEFINED];
export const useUpdateRequiredState = (state) => useMemo(() => updateRequiredStates.includes(state), [state]);
// show 'refresh' option' use when client setup not required
// no local data for any reason (error or server has no data)
export const noDataStates = [...updateRequiredStates, ...tryAgainStates, LoadState.EMPTY];
export const useNoDataState = (state) => useMemo(() => noDataStates.includes(state), [state]);
// show 'skeleton' - no local data and load is in progress
export const skeletonStates = [LoadState.INITIAL_LOAD, LoadState.EMPTY_UPDATE];
export const useSkeletonState = (state) => useMemo(() => skeletonStates.includes(state), [state]);
// (optional) show loading linear progress
export const loadingStates = [...skeletonStates, LoadState.READY_UPDATE];
export const useLoadingState = (state) => useMemo(() => loadingStates.includes(state), [state]);
// show main feature - data is ready
export const readyStates = [LoadState.READY, LoadState.READY_UPDATE, LoadState.READY_UPDATE_ERROR];
export const useReadyState = (state) => useMemo(() => readyStates.includes(state), [state]);

// recommended for most use cases
// isReady - (required) show main feature
// isNoData - (required) 'load/refresh' button
// isSkeleton - show skeleton instead of the data
// isLoading - optional non-blocking loading indicator
// isError - optional non-blocking error indicator
export const useLoadStates = (resourceSelectors, resourcesOverride) => {
  const loadState = useLoadState(resourceSelectors, resourcesOverride);
  const isNoData = useNoDataState(loadState);
  const isSkeleton = useSkeletonState(loadState);
  const isReady = useReadyState(loadState);
  const isLoading = useLoadingState(loadState);
  const isError = useErrorState(loadState);
  const loadStates = useMemo(
    () => ({ isNoData, isSkeleton, isReady, isLoading, isError }),
    [isNoData, isSkeleton, isReady, isLoading, isError],
  );
  return loadStates;
};

// recommended when user suppose to initiate/setup a resource (like 'click to setup')
// isEmpty - (required) no data on server side - handled by init/setup ux (like 'click to add/setup')
// isReady - (required) data is ready - show main feature
// isUpdateRequired - show 'try again/update/refresh' button
// isSkeleton - show skeleton
// isLoading - optional non-blocking loading indicator
// isError - optional non-blocking error indicator
export const useLoadSetupStates = (resourceSelectors, resourcesOverride) => {
  const loadState = useLoadState(resourceSelectors, resourcesOverride);
  const isUpdateRequired = useUpdateRequiredState(loadState);
  const isEmpty = useEmptyState(loadState);
  const isSkeleton = useSkeletonState(loadState);
  const isReady = useReadyState(loadState);
  const isLoading = useLoadingState(loadState);
  const isError = useErrorState(loadState);
  const loadStates = useMemo(
    () => ({ isUpdateRequired, isEmpty, isSkeleton, isReady, isLoading, isError }),
    [isUpdateRequired, isEmpty, isSkeleton, isReady, isLoading, isError],
  );
  return loadStates;
};

// (useLoadStates is recommended to use instead useLoadState)
// resourceSelectors should contain following methods to be fully compatible with this hook
// getLoadPending
// getIsLoading
// getError
// getResourcesById
export function useLoadState(resourceSelectors, resourcesOverride) {
  let loadState = LoadState.READY;

  // verify(resourceSelectors.getLoadPending, 'getLoadPending selector is not defined');
  const loadPending = useSelector((state) => resourceSelectors.getLoadPending?.(state));
  // verify(resourceSelectors.getIsLoading, 'getIsLoading selector is not defined');
  const isLoading = useSelector((state) => resourceSelectors.getIsLoading?.(state));
  // verify(resourceSelectors.getError, 'getError selector is not defined');
  const error = useSelector((state) => resourceSelectors.getError?.(state));
  const allResources = useSelector((state) => resourceSelectors.getResourcesById?.(state));
  const resources = resourcesOverride || allResources;
  verify(resources?.size != null, 'resourcesOverride or getResourcesById selector does not return a valid collection');

  if (loadPending && !isLoading && !resources?.size && !error) loadState = LoadState.UNDEFINED;
  else if (loadPending && isLoading && !resources?.size) loadState = LoadState.INITIAL_LOAD;
  else if (loadPending && isLoading && resources?.size) loadState = LoadState.READY_UPDATE;
  else if (loadPending && !isLoading && !resources?.size && error) loadState = LoadState.INITIAL_LOAD_ERROR;
  else if (loadPending && !isLoading && resources?.size && !error) loadState = LoadState.READY;
  else if (!loadPending && !isLoading && resources?.size === 0 && !error) loadState = LoadState.EMPTY;
  else if (!loadPending && isLoading && resources?.size === 0) loadState = LoadState.EMPTY_UPDATE;
  else if (!loadPending && !isLoading && resources?.size === 0 && error) loadState = LoadState.EMPTY_UPDATE_ERROR;
  else if (!loadPending && !isLoading && resources?.size && !error) loadState = LoadState.READY;
  else if (!loadPending && isLoading && resources?.size) loadState = LoadState.READY_UPDATE;
  else if (!loadPending && !isLoading && resources?.size && error) loadState = LoadState.READY_UPDATE_ERROR;
  else {
    verify(
      false,
      `unexpected state loadPending=${loadPending} isLoading=${isLoading} error=${error} resources.size=${resources?.size}`,
    );
  }

  return loadState;
}
export default useLoadState;
